import { useCallback, useContext } from 'react'
import { useAsyncFn } from 'react-use'
import { ContextShell } from '../../modules/context'

export type Announcement = {
  lang?: string | null
  image?: string | null
  title?: string | null
  name?: string | null
  tagLabel?: string | null
  button?: {
    url?: string | null
    label?: string | null
    newTab?: boolean
  }
  description?: string | null
  startAt?: string | null
  id?: string | null
}

type UseAnnouncements = {
  loading: boolean
  error?: Error | undefined
  value?: Announcement | undefined
  loadAnnouncements: () => Promise<Announcement | undefined>
  dismissAnnouncement: ({
    id,
    elements
  }: {
    id: string
    elements: string[]
  }) => Promise<void>
}

const isValidAnnouncement = (response: any, isEmployee: boolean): boolean => {
  if (!response?.announcement?.length) {
    return false
  }

  if (response?.announcement?.[0]?.name === 'Moises Live Launch') {
    if (isEmployee) {
      return true
    }

    if (typeof window === 'undefined') {
      return false
    }

    // @ts-ignore
    const isWindows = window?.navigator?.userAgentData?.platform
      ?.toLowerCase()
      ?.includes('win')

    if (!isWindows) {
      return false
    }
  }

  return true
}

export const useAnnouncements = (): UseAnnouncements => {
  const { user, MoisesCLI } = useContext(ContextShell)
  const isEmployee = user?.isEmployee === true

  const [state, loadAnnouncements] = useAsyncFn(async () => {
    if (MoisesCLI.isAuthenticated()) {
      const response = await MoisesCLI.getAnnouncements()

      if (!isValidAnnouncement(response, isEmployee)) {
        return undefined
      }

      return {
        ...(response.announcement?.[0]?.cmsPayload?.web?.sideBanner?.[0] || {}),
        id: response.announcement?.[0]?.id,
        startAt: response.announcement?.[0]?.startAt,
        name: response.announcement?.[0]?.name
      }
    }

    return undefined
  }, [MoisesCLI, isEmployee])

  const dismissAnnouncement = useCallback(
    async ({ id, elements }: { id: string; elements: string[] }) => {
      if (MoisesCLI.isAuthenticated()) {
        const response = await MoisesCLI.dismissAnnouncement({
          id,
          elements
        })
        return response
      }
      return {}
    },
    [MoisesCLI]
  )

  return {
    ...state,
    dismissAnnouncement,
    loadAnnouncements
  }
}
